import { NavLinkItem } from 'motion-components';
import React, { FC, useMemo } from 'react';
import { Nav, NavDropdown } from 'react-bootstrap';
import { generatePath } from 'react-router-dom';
import { getRouteByName } from './routes';
import { useRootStore } from 'store';
import i18n, { t } from 'i18next';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useUserPermissions } from 'shared-pages/hooks/user-permissions';
import { isAfisCyprus, isMedicycle, isRebatteryLiion } from 'shared-pages/common/constants';
import { commonMenuItems, menuItemsNames } from 'shared-pages/common/menu-items';

const regionsTitle = isAfisCyprus || isMedicycle ? 'Επαρχίες' : 'Περιφέρειες';

const MI = {
  ...menuItemsNames,
  COLBYMAT: 'Συλλεχθείσα Ποσότητα ανά Κατηγορία',
  COLBYMATBYCOLLECTOR: 'Συλλεχθείσα Ποσότητα ανά Κατηγορία ανά Συλλέκτη',
  COLBYMATBYREGION: 'Συλλεχθείσα Ποσότητα ανά Κατηγορία ανά Περιφερειακή Ενότητα',
  COLBYMATBYDA: 'Παραλαμβανόμενη Ποσότητα από Μονάδες Δευτερογενούς Αποθήκευσης',
  COLBYMATBYWRECPOINT: 'Παραλαμβανόμενη Ποσότητα από Μονάδες Ανακύκλωσης',
  //
  ALARMS: 'Alarms',
  ALARMS_PER_COLLECTOR: 'Φύλλο Αξιολόγησης Συλλέκτη-Μεταφορέα',
};

//import { t as T } from 'i18next';
// const t = function (str) {
//   console.log(`${str.toUpperCase()}: '${str}'`);
//   return T(str);
// };

const gqlAddContextlog = gql`
  mutation addContextlog($data: ContextlogInput) {
    addContextlog(data: $data) {
      id
    }
  }
`;

type Props = Record<string, never>;

const MyNavLinkItem = ({ text, routeName, disabled = false }) => {
  const { getUserCanView } = useUserPermissions();

  if (typeof routeName === 'object') {
    const rtName = routeName.routeName;
    const to = { ...routeName, pathname: getRouteByName(rtName) };
    const canView = getUserCanView(rtName);
    if (!canView || disabled) {
      return (
        <a className="ps-3 nav-link disabled" style={{ color: 'gray!important' }} href="/">
          {text}
        </a>
      );
    }
    return <NavLinkItem text={text} to={to} enable={canView} />;
  } else {
    const canView = getUserCanView(routeName);
    if (!canView || disabled) {
      return <a className="ps-3 nav-link disabled">{text}</a>;
    }
    return <NavLinkItem text={text} to={getRouteByName(routeName)} enable={canView} />;
  }
};

export const MainMenu: FC<Props> = () => {
  /*-------------------- Body -----------------------------------------------*/
  const { appStore, authStore } = useRootStore();
  const [addContextLog] = useMutation(gqlAddContextlog);

  const mi = useMemo(() => {
    return {
      ...commonMenuItems, // COMMON MENU ITEMS
      ...{
        ColByMat: (
          <MyNavLinkItem
            disabled={false}
            text={t(MI.COLBYMAT)}
            routeName={{ routeName: 'CollectedWeightByMaterial', state: { app: 'RE-BATTERY' } }}
          />
        ),
        ColByMatByCollector: (
          <MyNavLinkItem
            disabled={false}
            text={t(MI.COLBYMATBYCOLLECTOR)}
            routeName={{
              routeName: 'CollectedWeightByMaterialByCollector',
              state: { app: 'RE-BATTERY' },
            }}
          />
        ),
        ColByMatByRegion: (
          <MyNavLinkItem
            disabled={false}
            text={t(MI.COLBYMATBYREGION)}
            routeName={{
              routeName: 'CollectedWeightByMaterialByRegion',
              state: { app: 'RE-BATTERY' },
            }}
          />
        ),
        ColByMatByDa: (
          <MyNavLinkItem
            disabled={false}
            text={t(MI.COLBYMATBYDA)}
            routeName={{
              routeName: 'CollectedWeightByDa',
              state: { app: 'RE-BATTERY' },
            }}
          />
        ),
        ColByMatByWrecPoint: (
          <MyNavLinkItem
            disabled={false}
            text={t(MI.COLBYMATBYWRECPOINT)}
            routeName={{
              routeName: 'CollectedWeightByWrecPoint',
              state: { app: 'RE-BATTERY' },
            }}
          />
        ),
        // Alarms Menu
        Alarms: (
          <MyNavLinkItem
            text={t(MI.ALARMS)}
            routeName={{ routeName: 'Alarms', state: { app: 'RE-BATTERY' } }}
          />
        ),
        AlarmPerCollectorRep: (
          <MyNavLinkItem
            text={t(MI.ALARMS_PER_COLLECTOR)}
            routeName={{ routeName: 'AlarmsPerCollectorRep', state: { app: 'RE-BATTERY' } }}
          />
        ),
        // Ξαναχτίζονται με τη γλώσσα· τα κοινά items καλούν t() μία φορά στο φόρτωμα
        Contributions: (
          <MyNavLinkItem
            disabled={false}
            text={t('Εισφορές')}
            routeName={{ routeName: 'Contributions', state: {} }}
          />
        ),
        Contributors: (
          <MyNavLinkItem
            disabled={false}
            text={t('Υπόχρεοι')}
            routeName={{ routeName: 'Contributors', state: {} }}
          />
        ),
        ContributionsHistory: (
          <MyNavLinkItem
            disabled={false}
            text={t('Ιστορικό Δηλώσεων')}
            routeName={{ routeName: 'ContributionsHistory', state: {} }}
          />
        ),
        CollectionOrders: (
          <MyNavLinkItem
            disabled={false}
            text={t('Αιτήματα Συλλογής')}
            routeName={{ routeName: 'Orders', state: { app: 'WBMANAGER' } }}
          />
        ),
        Users: <NavLinkItem text={t('Χρήστες')} to={getRouteByName('Users')} enable={true} />,
      },
    };
  }, [i18n.language]);

  /* --------------- Platform Menu ------------------- */
  const menuPlatformOwner = () => {
    return (
      <>
        {/****  ΑΡΧΕΙΑ ****/}
        <NavDropdown title={t(MI.FILES)} id="files-nav-dropdown">
          {mi.Materials}
          {mi.ContainerTypes}
          {mi._________}
          {mi.Regions}
          {mi.Subregions}
          {mi.Nomoi}
          {mi._________}
          {mi.Vehicles}
          {mi.Drivers}
          {mi.EdocSeries}
          {mi._________}
          {mi.Collectors}
          {mi.WasteReceptionPoints}
          {mi.WasteReceptionPointsDa}
          {mi._________}
          {mi.Users}
        </NavDropdown>

        {/****  Σημεία Συλλογής ****/}
        <NavDropdown title={t(MI.COLLECTION_POINTS)} id="collections-nav-dropdown">
          {mi.Collectionpoints}
          {mi.Notifications}
          {mi._________}
          {mi.CollectionpointCategories}
          {mi.CpWasteCategories}
          {mi._________}

          {mi.CollectionpointsStats}
          {mi.CollectionpointsMap}
          {/* {mi.ContainersQrCodes} */}
          {/* {mi._________} */}
          {/* {mi.CollectionpointRequests} */}
          {/* {mi.ContainerRequests} */}
        </NavDropdown>

        {/****  Συλλογές ****/}
        <NavDropdown title={t(MI.COLLECTIONS)} id="collections-nav-dropdown">
          {mi.CollectionOrders}
          {mi.CollectionOrdersStatuses}
          {/* {mi.Routes} */}
          {mi.Collections}
          {mi.InternalTransactions}
          {/* {mi._________}
          {mi.WrsTransactions}
          {mi._________}
          {mi.DriverAppEntries} */}
          {mi._________}
          {mi.Idocs}
          {mi.Idocs4FinalDest}
          {mi._________}
          {mi.Alarms}
        </NavDropdown>

        {/****  Αναφορές ****/}
        <NavDropdown title={t(MI.REPORTS)} id="collections-nav-dropdown">
          {mi.ColByMat}
          {mi.ColByMatByCollector}
          {mi.ColByMatByRegion}
          {mi.ColByMatByDa}
          {mi.ColByMatByWrecPoint}
          {mi._________}
          {mi.AlarmPerCollectorRep}
          {mi._________}
          {mi.CollectionsRepDgrmReport}
          {mi.CollectionsPivotReport}
        </NavDropdown>

        {/****  Εισφορές ****/}
        <NavDropdown title={t('Εισφορές')} id="reports-nav-dropdown">
          {mi.Contributions}
          {mi.Contributors}
          {mi._________}
          <NavLinkItem
            text={t('Ετήσιες δηλωθείσες εισφορές Υπόχρεου ανά κατηγορία εισφοράς')}
            to={getRouteByName('YearlyContributions')}
            enable={appStore.isLogIn}
          />
          <NavLinkItem
            text={t('Σύνολο Εισφορών ανά Κατηγορία')}
            to={getRouteByName('ContributionItemsSums')}
            enable={appStore.isLogIn}
          />
          <NavLinkItem
            text={t('Εισφορές ανά Κατηγορία και Υπόχρεο Αναλυτικά')}
            to={getRouteByName('ContributionItemDetails')}
            enable={appStore.isLogIn}
          />
          <NavLinkItem
            text={t('Ετήσια Αθροίσματα Εισφορών ανά Υπόχρεο')}
            to={getRouteByName('YearlyContributorsContributions')}
            enable={appStore.isLogIn}
          />
          {mi.ContributionsHistory}
          {mi._________}
          {/* Αρχεία κοστολόγησης: είδη από τη φόρμα, templates μόνο προβολή */}
          <NavDropdown title={t('Αρχεία')} id="cnt-files-nav-dropdown" drop="end">
            <MyNavLinkItem text={t('Είδη Εισφορών')} routeName="CntItems" />
            <MyNavLinkItem text={t('Template Εισφορών')} routeName="CntContributionTemplates" />
          </NavDropdown>
        </NavDropdown>

        {/****  Διαχείριση ****/}
        <NavDropdown title={t(MI.ADMINISTRATION)} id="collections-nav-dropdown">
          {mi.Audittrail}
          {mi.Mails}
          {mi.Logbook}
          {mi._________}
          {mi.Params}
          {mi._________}
          {mi.UsersStats}
        </NavDropdown>
      </>
    );
  };

  /* --------------- WasteReceptionPoint Menu ------------------- */
  const menuWasteReceptionPoint = () => {
    return <></>;
  };

  /* --------------- Collection Points Menu ------------------- */
  const menuCollectionpoints = () => {
    return (
      <>
        {mi.CollectionOrders}
        <NavLinkItem
          text={t('Στοιχεία Σημείου Συλλογής')}
          to={generatePath(getRouteByName('Collectionpoint'), { id: appStore.associatedId })}
          enable={true}
        />
        {/* {mi.Users} */}
      </>
    );
  };

  /* --------------- Collectors Menu ------------------- */
  const menuCollectors = () => {
    return (
      <>
        <NavDropdown title={t(MI.FILES)} id="files-nav-dropdown">
          {mi.Vehicles}
          {mi.Drivers}
          {mi.EdocSeries}
          {mi._________}
          <NavLinkItem
            text={t(MI.COLLECTOR)}
            to={generatePath(getRouteByName('Collector'), { id: appStore.associatedId ?? 0 })}
            enable={true}
          />
          {mi._________}
          {mi.Users}
        </NavDropdown>

        {/****  Σημεία Συλλογής ****/}
        {mi.Collectionpoints}
        {/*}
        <NavDropdown title={t(MI.COLLECTION_POINTS)} id="collections-nav-dropdown">
          {mi.Collectionpoints}
          {mi.CollectionpointsStats}
          {mi.CollectionpointsMap}
          {mi.ContainersQrCodes}
        </NavDropdown> */}

        {/****  Συλλογές ****/}
        <NavDropdown title={t(MI.COLLECTIONS)} id="collections-nav-dropdown">
          {mi.CollectionOrders}
          {/* {mi.Routes} */}
          {mi.Collections}
          {/* {mi.InternalTransactions}
          {mi._________}
          {mi.WrsTransactions}
          {mi._________}

          {mi.DriverAppEntries} */}
          {mi._________}
          {mi.Idocs}
          {/* {mi.Idocs4FinalDest} */}
          {mi._________}
          {mi.Alarms}
        </NavDropdown>

        {/****  Αναφορές ****/}
        <NavDropdown title={t(MI.REPORTS)} id="collections-nav-dropdown">
          {mi.CollectionsRepDgrmReport}
        </NavDropdown>
      </>
    );
  };

  /* --------------- CollectorDrivers Menu ------------------- */
  const menuCollectorDrivers = () => {
    return (
      <>
        <NavDropdown title={t(MI.FILES)} id="files-nav-dropdown">
          {mi.Vehicles}
        </NavDropdown>

        {/****  Σημεία Συλλογής ****/}
        {/* <NavDropdown title={t(MI.COLLECTION_POINTS)} id="collections-nav-dropdown">
          {mi.Collectionpoints}
          {mi.CollectionpointsMap}
        </NavDropdown> */}

        {/****  Συλλογές ****/}
        <NavDropdown title={t(MI.COLLECTIONS)} id="collections-nav-dropdown">
          {mi.Collections}
          {mi.Routes}
          {mi.DriverAppEntries}
          {mi.Idocs}
        </NavDropdown>
      </>
    );
  };

  /* --------------- Contributors Menu ------------------- */
  const menuContributors = () => {
    return (
      <>
        {mi.Contributions}
        <NavLinkItem
          text={t('Ετήσια Αθροίσματα Εισφορών')}
          to={getRouteByName('YearlyContributions')}
          enable={appStore.isLogIn}
        />
        <NavLinkItem
          text={t('Ετήσια Αναφορά ΕΜΠΑ')}
          to={getRouteByName('YearlyEmpaContributions')}
          enable={appStore.isLogIn}
        />
        {mi.ContributionsHistory}
        {mi.CollectionOrders}
        {mi.Users}
      </>
    );
  };

  /* --------------- Collectors Help Menu ------------------- */

  const switchMenu = () => {
    if (appStore.isPlatformOwner) return menuPlatformOwner();
    else if (appStore.isCollector) return menuCollectors();
    else if (appStore.isCollectorDriver) return menuCollectorDrivers();
    else if (appStore.isWasteReceptionPoint) return menuWasteReceptionPoint();
    else if (appStore.isCollectionPoint) return menuCollectionpoints();
    else if (appStore.isContributor) return menuContributors();
    else return <></>;
  };

  /*-------------------- Render ---------------------------------------------*/
  return (
    // -- Menu --
    <Nav className="mr-auto">{switchMenu()}</Nav>
  );
};
